import "./App.css";
import "./theme.css";
import "./login.css";

import { useState, useEffect, useRef, memo, useCallback } from "react";

import Typography from "@mui/material/Typography";

import { useTheme, ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MailIcon from "@mui/icons-material/Mail";
import ForumIcon from "@mui/icons-material/Forum";
import { ReloadIcon, HighlighterIcon } from "./icons/CustomIcons";
import Badge from "@mui/material/Badge";
import { neutralHoverSx, constructiveHoverSx, destructiveHoverSx } from "./iconButtonStyles";

import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";

import DeleteIcon from "@mui/icons-material/Delete";
import InsightsIcon from "@mui/icons-material/Insights";
import SettingsIcon from "@mui/icons-material/Settings";
import AutoAwesomeMosaicIcon from "@mui/icons-material/AutoAwesomeMosaic";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import ScheduleIcon from "@mui/icons-material/Schedule";

import { Authenticator } from "@aws-amplify/ui-react";
import { SignOut } from "@aws-amplify/ui-react/dist/types/components/Authenticator/Authenticator";
import "@aws-amplify/ui-react/styles.css";

import TourIcon from "@mui/icons-material/Tour";
import Button from "@mui/material/Button";
import ProjectPicker from "./ProjectPicker";

import {
  BrowserRouter as Router,
  Route,
  Routes,
  useParams,
} from "react-router-dom";

import FavoriteIcon from "@mui/icons-material/Favorite";

import PhotoIcon from "@mui/icons-material/Photo";
import CropOriginalIcon from "@mui/icons-material/CropOriginal";

import TurnedInIcon from "@mui/icons-material/TurnedIn";
import TurnedInNotIcon from "@mui/icons-material/TurnedInNot";
import ShareIcon from "@mui/icons-material/Share";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./toast.css";

import init from "jsriki";

import { useAtom, useAtomValue, useSetAtom } from "jotai";

import store from "store2";
import PestControlIcon from "@mui/icons-material/PestControl";
import { refreshToken, switchStage } from "./Auth";

import { Main, AppBar, DrawerHeader } from "./AppStyled";
import { MessageItem } from "./MessageItem";
import { Logo } from "./Logo";
import { ViewOptionsMenu } from "./ViewOptionsMenu";
import { AppBarSearch } from "./AppBarSearch";

import {
  labelToggleState,
  debugToggleState,
  imageToggleAtom,
  projectPickerState,
  timeFilterState,
  mergedStoriesState,
  joinStoriesState,
  reloadState,
  hightlightToggleAtom,
  showSelectedStoriesState,
  selectedCategory,
  MenuCategory,
  menuCategoriesList,
  displayedStoryCountState,
  themeModeState,
  appBarCustomContentState,
  appBarSearchContentState,
} from "./JotaiAtoms";
import { lightTheme, darkTheme, getEffectiveTheme } from "./themeConfig";

import * as lodash from "lodash";

import GraphView from "./StoryGraph";
import UserGraph from "./UserGraph";

import { ComposeArea } from "./ComposeArea";
import { Search } from "./Search";
import { StoryLabelFilterBox, LabelsData } from "./StoryLabelFilterBox";
import { Thread } from "./Thread";
import { Settings } from "./Settings";
import log from "./logger";
import { FetchStories, getCheckedThreads } from "./FetchStories";

import * as u from "./utility";

import { useHttpPostAndProject, useStage, useLocalStorage } from "./hooks";
import { QuickSearch } from "./Search";
import { DarkStoryPreview } from "./StoryDetails";
import { People, PeopleData, PeopleControls } from "./People";
import { PeopleList } from "./PeopleList";
import BubbleChartIcon from "@mui/icons-material/BubbleChart";

const MIN_DRAWER_WIDTH = 177;
const MAX_DRAWER_WIDTH = 400;
const DEFAULT_DRAWER_WIDTH = 177;

type RetryToastVerticalProps = {
  message: string;
  onRetry?: (e: React.MouseEvent<HTMLElement>) => void;
  onUndo?: (e: React.MouseEvent<HTMLElement>) => void;
  children?: any;
};

function RetryToastVertical({
  message,
  onRetry,
  onUndo,
  children,
}: RetryToastVerticalProps) {
  return (
    <Stack direction={"column"} sx={{ width: "100%", gap: 1.5 }}>
      <Typography sx={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.5 }}>
        {message}
      </Typography>
      {children && (
        <Box sx={{ maxHeight: "300px", overflow: "auto" }}>{children}</Box>
      )}
      <Stack direction={"row"} gap={1}>
        {onRetry && (
          <Button
            onClick={onRetry}
            size="small"
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 500,
              px: 1.5,
              py: 0.5,
              minWidth: 'auto',
              color: 'var(--accent-primary)',
              '&:hover': {
                backgroundColor: 'var(--surface-hover)',
              },
            }}
          >
            Retry
          </Button>
        )}
        {onUndo && (
          <Button
            onClick={onUndo}
            size="small"
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 500,
              px: 1.5,
              py: 0.5,
              minWidth: 'auto',
              color: 'var(--accent-primary)',
              '&:hover': {
                backgroundColor: 'var(--surface-hover)',
              },
            }}
          >
            Undo
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

type RetryToastHorizontalProps = {
  message: string;
  onRetry?: (e: React.MouseEvent<HTMLElement>) => void;
  onUndo?: (e: React.MouseEvent<HTMLElement>) => void;
  children?: any;
};
function RetryToastHorizontal({
  message,
  onRetry,
  onUndo,
  children,
}: RetryToastHorizontalProps) {
  return (
    <Stack direction={"row"} sx={{ width: "100%", alignItems: 'center', justifyContent: 'space-between' }}>
      <Stack direction={"row"} sx={{ alignItems: 'center', gap: 1, flex: 1 }}>
        <Typography sx={{ fontSize: '13px', color: 'var(--text-primary)' }}>
          {message}
        </Typography>
        {children}
      </Stack>
      <Stack direction={"row"} gap={1}>
        {onRetry && (
          <Button
            onClick={onRetry}
            size="small"
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 500,
              px: 1.5,
              py: 0.5,
              minWidth: 'auto',
              color: 'var(--accent-primary)',
              '&:hover': {
                backgroundColor: 'var(--surface-hover)',
              },
            }}
          >
            Retry
          </Button>
        )}
        {onUndo && (
          <Button
            onClick={onUndo}
            size="small"
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 500,
              px: 1.5,
              py: 0.5,
              minWidth: 'auto',
              color: 'var(--accent-primary)',
              '&:hover': {
                backgroundColor: 'var(--surface-hover)',
              },
            }}
          >
            Undo
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

function ShowSelectedStoriesButton() {
  const [selected, setSelected] = useAtom(joinStoriesState);
  const [numSelected, setNumSelected] = useState(0);
  const setShowSelectedStories = useSetAtom(showSelectedStoriesState);

  useEffect(() => {
    setNumSelected(getCheckedThreads(selected).length);
  }, [selected]);

  const handleClick = () => {
    setShowSelectedStories(true);
  };

  const handleClear = () => {
    setSelected({});
  };

  const hasSelection = numSelected > 0;

  return (
    <>
      {/* Show Selected - primary indicator with badge and blue color */}
      <Tooltip title={hasSelection ? `Show ${numSelected} selected` : "Show selected"}>
        <span>
          <Button
            variant={hasSelection ? "contained" : "text"}
            size="small"
            disabled={!hasSelection}
            onClick={handleClick}
            startIcon={
              hasSelection ? (
                <Badge color="default" badgeContent={numSelected} sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: 'var(--accent-primary)',
                    fontWeight: 600,
                  }
                }}>
                  <CheckBoxIcon />
                </Badge>
              ) : (
                <CheckBoxIcon />
              )
            }
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              minWidth: 'auto',
              px: hasSelection ? 2 : 1.5,
              backgroundColor: hasSelection ? 'var(--accent-primary)' : 'transparent',
              color: hasSelection ? '#ffffff' : 'var(--text-secondary)',
              '&:hover': {
                backgroundColor: hasSelection ? 'var(--accent-primary-hover)' : 'var(--surface-hover)',
              },
              '&:disabled': {
                backgroundColor: 'transparent',
                color: 'var(--text-tertiary)',
              },
            }}
          >
            {hasSelection ? 'Show' : 'Selected'}
          </Button>
        </span>
      </Tooltip>

      {/* Clear Selection - text button, appears when selection exists */}
      {hasSelection && (
        <Tooltip title="Clear selection">
          <Button
            variant="text"
            size="small"
            onClick={handleClear}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              minWidth: 'auto',
              px: 1.5,
              color: 'var(--text-secondary)',
              '&:hover': {
                backgroundColor: 'var(--surface-hover)',
              },
            }}
          >
            Clear
          </Button>
        </Tooltip>
      )}
    </>
  );
}

type JoinButtonProps = {
  onJoin: (threads: string[]) => void;
  onUndo: () => void;
  sx: any;
};

const JoinButton = ({ onJoin, onUndo, sx }: JoinButtonProps) => {
  const [selected, setSelected] = useAtom(joinStoriesState);
  const [numSelected, setNumSelected] = useState(0);
  const [httpPost, project] = useHttpPostAndProject();

  useEffect(() => {
    setNumSelected(getCheckedThreads(selected).length);
  }, [selected]);

  const handleJoinThreads = () => {
    const threads = getCheckedThreads(selected);
    if (!threads.length) {
      return;
    }

    const disjoinThreads = async (
      story: string,
      threads: string[],
      dryrun?: boolean
    ) => {
      if (threads.length > 0) {
        try {
          if (!dryrun) {
            await httpPost("label", {
              disjoin: { threads: threads, story: story },
            });
          }

          if (onUndo) {
            onUndo();
          }
        } catch (err) {}
      }
    };

    const joinThreads = async (stories: string[]) => {
      const attemptJoin = async (stories: string[]) => {
        try {
          const response = await httpPost("label", {
            join: { stories: stories, dryrun: true },
          });
          const joined = response.data.join;

          if (joined.length > 0) {
            let undone = false;
            const undoDryRun = () => {
              disjoinThreads(joined[0], joined.slice(1), true);
              undone = true;
            };

            const handleCommitJoin = async () => {
              if (!undone) {
                const response = await httpPost("label", {
                  join: { stories: stories, dryrun: false },
                });
                const joined = response.data.join;
                if (joined.length > 0) {
                  const undo = () => {
                    disjoinThreads(joined[0], joined.slice(1), false);
                  };

                  if (joined.length !== stories.length) {
                    let left = [joined[0]];
                    for (let s of stories) {
                      if (!joined.includes(s)) {
                        left.push(s);
                      }
                    }

                    const message = `We could not merge ${left.length} stories. Perhaps, it's too much work to do in one go.`;
                    const retry = () => attemptJoin(left);

                    if (joined.length > 1) {
                      toast.warning(
                        <RetryToastHorizontal
                          message={message}
                          onRetry={retry}
                          onUndo={undo}
                        />
                      );
                    } else {
                      toast.warning(
                        <RetryToastHorizontal
                          message={message}
                          onRetry={retry}
                        />
                      );
                    }
                  }
                }
              }
            };

            const mainStoryId = joined[0];
            const req = { stories: true, story: mainStoryId };
            const resp = await httpPost("stories", req);
            const storyData = resp.data.stories[0];

            toast.info(
              <RetryToastVertical
                message={`Joining ${stories.length} stories!`}
                onUndo={undoDryRun}
              >
                <DarkStoryPreview item={storyData} />
              </RetryToastVertical>,
              {
                onClose: handleCommitJoin,
                pauseOnFocusLoss: false,
                autoClose: 2000,
                icon: false,
              }
            );
          }

          onJoin(joined);
        } catch (err) {}
      };

      await attemptJoin(stories);
      setSelected({});
    };

    joinThreads(threads);
  };

  return (
    <Tooltip title="Join stories">
      <span>
        <IconButton
          color="inherit"
          onClick={handleJoinThreads}
          edge="start"
          disabled={numSelected < 2}
          disableRipple
          sx={{
            ...constructiveHoverSx,
            color: 'inherit',
            ...sx
          }}
        >
          <ForumIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
};

type BulkDeleteProps = {
  onDelete?: (stories: string[]) => void;
  onUndo?: () => void;
  sx?: any;
};

const BulkDelete = ({ onDelete, onUndo, sx }: BulkDeleteProps) => {
  const [selected, setSelected] = useAtom(joinStoriesState);
  const [numSelected, setNumSelected] = useState(0);
  const [httpPost, project] = useHttpPostAndProject();

  useEffect(() => {
    setNumSelected(getCheckedThreads(selected).length);
  }, [selected]);

  const handleDelete = () => {
    const threads = getCheckedThreads(selected);
    if (!threads.length) {
      return;
    }

    const deleteBulk = async (stories: string[]) => {
      const restoreBulk = async (stories: string[]) => {
        try {
          const response = await httpPost("label", {
            restore: { stories: stories },
          });
          const restored = response.data.restore;
          if (restored.length > 0) {
            setSelected((prev: any) => {
              let next = prev ? { ...prev } : {};
              for (let d of restored) {
                next[d] = true;
              }
              return next;
            });
          }
          if (onUndo) {
            onUndo();
          }
        } catch (err) {}
      };

      try {
        const response = await httpPost("label", {
          delete: { stories: stories },
        });
        const deleted = response.data.delete;
        if (deleted.length > 0) {
          setSelected((prev) => {
            let res = prev ? { ...prev } : {};
            for (let d of deleted) {
              delete res[d];
            }
            return res;
          });

          let message = "Successfully deleted ";
          if (deleted.length > 1) {
            message += `${deleted.length} stories.`;
          } else {
            message += "1 story.";
          }

          if (onDelete) {
            onDelete(deleted);
          }

          toast.success(
            <RetryToastHorizontal
              message={message}
              onUndo={() => restoreBulk(deleted)}
            />
          );
        }
      } catch (err) {}
    };

    deleteBulk(threads);
  };

  return (
    <Tooltip title="Delete stories">
      <span>
        <IconButton
          color="inherit"
          onClick={handleDelete}
          edge="start"
          disabled={numSelected < 1}
          disableRipple
          sx={{
            ...destructiveHoverSx,
            color: 'inherit',
            ...sx
          }}
        >
          <DeleteIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
};


function getCategoryToShow(type?: string): MenuCategory {
  if (type === "story") {
    return "Stories";
  } else if (type === "message") {
    return "Artifacts";
  } else {
    return (u.readPageStorage("Home", "Stories") || "Stories") as MenuCategory;
  }
}

function Home({ signOut }: { signOut?: SignOut }) {
  const theme = useTheme();
  const [open, setOpen] = useLocalStorage("AppBarOpened", true);
  const { type, proj, id, ts } = useParams();
  const topicFilter = type === "story" ? id : undefined;
  const messageFilter = type === "message" ? id : undefined;
  const projectFilter = type === "project" || proj ? proj : undefined;
  const [selected, setSelected] = useAtom(selectedCategory);
  const [newLabel, setNewLabel] = useState<string>();
  const [collapseAll, setCollapseAll] = useState(0);
  const [project, setProject] = useAtom(projectPickerState);
  const setReload = useSetAtom(reloadState);
  const setMergedStories = useSetAtom(mergedStoriesState);
  const [drawerWidth, setDrawerWidth] = useLocalStorage(
    "DrawerWidth",
    DEFAULT_DRAWER_WIDTH
  );
  const ref = useRef<HTMLDivElement>(null);

  const [imageToggle, setImageToggle] = useAtom(imageToggleAtom);
  const [highlightToggle, setHighlightToggle] = useAtom(hightlightToggleAtom);
  const [labelToggle, setLabelToggle] = useAtom(labelToggleState);
  const [debugToggle, setDebugToggle] = useAtom(debugToggleState);
  const displayedStoryCount = useAtomValue(displayedStoryCountState);

  useEffect(() => {
    // [211]
    refreshToken(
      () => {},
      () => {
        if (signOut) {
          signOut();
        }
      }
    );
  }, []);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleDataReload = () => {
    setReload((prev) => prev + 1);
  };

  const handleStoriesJoined = (stories: string[]) => {
    setMergedStories(stories);
  };

  const handleCollapseAll = () => {
    setCollapseAll(collapseAll + 1);
  };

  const handleSignOut = () => {
    if (signOut) {
      signOut();
    }
  };

  const handleLabelCreated = useCallback(
    (newLabel: string) => {
      setNewLabel(newLabel);
    },
    [setNewLabel]
  );

  const handleSelectCategory = (cat: MenuCategory) => {
    if (messageFilter || topicFilter) {
      const url = u.getProjectUrl(project);
      window.location.href = url;

      const qualifiedKey = `Home:/${project}${window.location.search}`;
      store(qualifiedKey, cat);
    } else if (!type) {
      setSelected(cat);
      u.writePageStorage("Home", cat);
    }
  };

  const handleLogoClicked = () => {
    const url = project ? u.getProjectUrl(project) : u.getHomeUrl();
    if (window.location.href !== url) {
      window.location.href = url;
    } else {
      handleDataReload();
    }
  };

  const handleFetch = useCallback((changed: boolean) => {
    if (ref && ref.current && changed) {
      setTimeout(() => {
        if (ref && ref.current) {
          ref.current.scrollIntoView({
            behavior: "auto",
            block: "start",
            inline: "start",
          });
        }
      }, 300);
    }
  }, []);

  useEffect(() => {
    let newProject: string = project;
    if (topicFilter || messageFilter || projectFilter) {
      newProject = proj || "";
    }

    if (project !== newProject) {
      setProject(newProject);
    }

    document.title = `topcat | ${project}`;
  }, [project]);

  useEffect(() => {
    setSelected(getCategoryToShow(type));
  }, []);

  const showFilters = () => {
    return (
      selected === "Stories" ||
      selected === "Popular" ||
      selected === "Recent" ||
      selected === "Graph" ||
      selected === "Settings" // because updating queries in settings will result in filters being reload
    );
  };

  const showStoryControls = () => {
    return (
      selected === "Stories" ||
      selected === "Popular" ||
      selected === "Recent" ||
      selected === "Events"
    );
  };

  const showTimeFilters = () => {
    return (
      selected === "Stories" ||
      selected === "Popular" ||
      selected === "Recent" ||
      selected === "Artifacts" ||
      selected === "Graph"
    );
  };

  const handleStoryClicked = (storyId: string) => {
    if (project) {
      const url = u.getStoryUrlFromId(storyId, project);
      window.open(url, "_blank", "noreferrer");
    }
  };

  const handleDrawerResizeMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    const handleMouseUp = () => {
      document.removeEventListener("mouseup", handleMouseUp, true);
      document.removeEventListener("mousemove", handleMouseMove, true);
    };

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = e.clientX - document.body.offsetLeft;
      setDrawerWidth(
        lodash.clamp(newWidth, MIN_DRAWER_WIDTH, MAX_DRAWER_WIDTH)
      );
    };

    document.addEventListener("mouseup", handleMouseUp, true);
    document.addEventListener("mousemove", handleMouseMove, true);
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <>
      <GlobalStyles styles={{
        html: { overflow: 'hidden', height: '100%' },
        body: { overflow: 'hidden', height: '100%' }
      }} />
      <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
        <CssBaseline />
      <QuickSearch />
      <Drawer
        PaperProps={{
          sx: {
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1f1f1f' : '#f8fafb',
            borderRight: 'none',
            boxShadow: 'none',
            // Custom scrollbar styling - ultra thin and barely visible
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(232, 234, 237, 0.08)'
                : 'rgba(95, 99, 104, 0.08)',
              borderRadius: '2px',
              transition: 'background 0.2s ease',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(232, 234, 237, 0.2)'
                : 'rgba(95, 99, 104, 0.2)',
            },
            // Firefox scrollbar styling
            scrollbarWidth: 'thin',
            scrollbarColor: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(232, 234, 237, 0.08) transparent'
              : 'rgba(95, 99, 104, 0.08) transparent',
          }
        }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
        ref={ref}
      >
        <Box
          sx={{
            position: "relative",
            display: "inline-block",
            width: "100%",
            height: "100%",
            overflowX: "clip",
          }}
        >
          <Box
            onMouseDown={(e) => handleDrawerResizeMouseDown(e)}
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              zIndex: 100,
              width: "8px",
              height: "100%",
              cursor: "ew-resize",
              background: 'transparent',
              transition: 'background 0.2s ease',
              '&:hover': {
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'linear-gradient(to right, transparent, rgba(138, 180, 248, 0.12))'
                  : 'linear-gradient(to right, transparent, rgba(25, 103, 210, 0.08))',
              },
            }}
          />

          {true && (
            <>
              <DrawerHeader>
                <Box sx={{ cursor: 'pointer' }} onClick={handleLogoClicked}>
                  <Logo height={30} />
                </Box>
              </DrawerHeader>

              <SideMenu
                selected={selected}
                setSelected={handleSelectCategory}
              />
              {showTimeFilters() && !topicFilter && !messageFilter && (
                <>
                  <Box sx={{ mt: 1.5 }} />
                  <TimeFilters />
                </>
              )}

              <LabelsData />
              {showFilters() && (
                <>
                  <Box sx={{ mt: 1.5 }} />
                  <Box
                    sx={{
                      pr: 2,
                      display:
                        topicFilter || messageFilter ? "none" : undefined,
                    }}
                  >
                    <StoryLabelFilterBox newLabel={newLabel} />
                  </Box>
                </>
              )}
              <PeopleData />
            </>
          )}
        </Box>
      </Drawer>
      <Main open={open} drawerWidth={drawerWidth}>
        <AppBar>
          <Toolbar sx={{
            minHeight: '64px !important',
            px: 2,
            display: 'flex',
            gap: 1,
            overflow: 'hidden',
            backgroundColor: 'transparent',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                sx={{
                  ...neutralHoverSx,
                  mr: 2,
                  ...(open && { display: "none" })
                }}
              >
                <MenuIcon />
              </IconButton>

              <IconButton
                color="inherit"
                onClick={handleDrawerClose}
                edge="start"
                sx={{
                  ...neutralHoverSx,
                  mr: 2,
                  ...(!open && { display: "none" })
                }}
              >
                {theme.direction === "ltr" ? (
                  <ChevronLeftIcon />
                ) : (
                  <ChevronRightIcon />
                )}
              </IconButton>

              <Box
                sx={{
                  mr: 3,
                  cursor: 'pointer',
                  ...(open && { display: "none" }),
                }}
                onClick={handleLogoClicked}
              >
                <Logo height={30} />
              </Box>

              {/* Left side - Context actions */}
              {(showStoryControls() ||
                selected === "Graph" ||
                selected === "Artifacts" ||
                selected === "Settings" ||
                selected === "Community") && (
                <Tooltip title="Reload">
                  <IconButton
                    color="inherit"
                    onClick={handleDataReload}
                    edge="start"
                    sx={{
                      ...neutralHoverSx,
                      mr: 1,
                    }}
                  >
                    <ReloadIcon />
                  </IconButton>
                </Tooltip>
              )}

              {selected === "People" && <PeopleControls />}

              {/* Highlight mode toggle */}
              {showStoryControls() && (
                <Tooltip title={highlightToggle ? "Read mode (click to annotate)" : "Highlight mode (click to read)"}>
                  <IconButton
                    color="inherit"
                    onClick={() => setHighlightToggle(!highlightToggle)}
                    edge="start"
                    sx={{
                      ...neutralHoverSx,
                      mr: 1,
                      color: highlightToggle
                        ? (theme) => theme.palette.mode === 'dark' ? '#fbbf24' : '#f59e0b'
                        : 'inherit',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.35rem',
                      },
                    }}
                  >
                    <HighlighterIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Search bar */}
            {useAtomValue(appBarSearchContentState) || (
              (selected === "Stories" || selected === "Popular" || selected === "Recent" || selected === "Deleted") && (
                <Box sx={{ flex: '1 1 auto', minWidth: 200, maxWidth: 500, mr: 2 }}>
                  <AppBarSearch />
                </Box>
              )
            )}

            {/* Spacer */}
            <Box sx={{ flexGrow: 1, minWidth: 8 }} />

            {/* Story count */}
            {showStoryControls() && displayedStoryCount > 0 && (
              <Typography
                sx={{
                  fontSize: '13px',
                  fontFamily: 'monospace',
                  color: 'var(--text-tertiary)',
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(232, 234, 237, 0.05)'
                    : 'rgba(0, 0, 0, 0.03)',
                  borderRadius: '12px',
                  flexShrink: 0,
                  mr: 1.5,
                }}
              >
                {displayedStoryCount} {displayedStoryCount === 1 ? 'story' : 'stories'}
              </Typography>
            )}

            {/* Custom page-specific controls */}
            {useAtomValue(appBarCustomContentState)}

            {/* Right side - Selection actions */}
            {showStoryControls() && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mr: 2,
                px: 1,
                borderRadius: '24px',
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(232, 234, 237, 0.05)'
                  : 'rgba(0, 0, 0, 0.03)',
                flexShrink: 0,
              }}>
                <ShowSelectedStoriesButton />
                <JoinButton
                  onJoin={handleStoriesJoined}
                  onUndo={handleDataReload}
                  sx={{ ml: 0, mr: 0 }}
                />
                <BulkDelete
                  onDelete={handleDataReload}
                  onUndo={handleDataReload}
                  sx={{ mr: 0 }}
                />
              </Box>
            )}

            {/* View options */}
            {(showStoryControls() || selected === "Artifacts") && (
              <Box sx={{ flexShrink: 0 }}>
                <ViewOptionsMenu
                  imageToggle={imageToggle}
                  labelToggle={labelToggle}
                  debugToggle={debugToggle}
                  onImageToggle={() => setImageToggle(!imageToggle)}
                  onLabelToggle={() => setLabelToggle(!labelToggle)}
                  onDebugToggle={() => setDebugToggle(!debugToggle)}
                  onCollapseAll={handleCollapseAll}
                  showCollapseAll={showStoryControls()}
                />
              </Box>
            )}

            {/* Project picker */}
            {!topicFilter && !messageFilter && (
              <Box sx={{ minWidth: 150, maxWidth: 300, mr: 2, flexShrink: 0 }}>
                <ProjectPicker />
              </Box>
            )}

            {/* Sign out */}
            <Button
              variant="text"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                py: 0.75,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                color: (theme) => theme.palette.mode === 'dark' ? '#9aa0a6' : '#5f6368',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(232, 234, 237, 0.08)'
                    : '#f1f3f4',
                  color: (theme) => theme.palette.mode === 'dark' ? '#e8eaed' : '#202124',
                },
              }}
              onClick={handleSignOut}
            >
              Sign out
            </Button>
          </Toolbar>
        </AppBar>
        <Box id="main-scroll-container" sx={{ flex: 1, overflow: 'auto' }}>
          <ToastContainer
            position="top-right"
            autoClose={4000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            style={{ width: "420px" }}
          />
          {selected === "Stories" && (
            <FetchStories
              collapseAll={collapseAll}
              filterId={topicFilter}
              timestampRef={ts}
              onLabelCreated={handleLabelCreated}
              onFetch={handleFetch}
            />
          )}
          {selected === "Artifacts" && <RawMessages filterId={messageFilter} />}
            {selected === "Deleted" && (
            <MemoFetchStories
              collapseAll={collapseAll}
              filterId={topicFilter}
              timestampRef={ts}
              deleted={true}
              onFetch={handleFetch}
            />
          )}
          {selected === "Popular" && (
            <MemoFetchStories
              collapseAll={collapseAll}
              filterId={topicFilter}
              timestampRef={ts}
              popular={true}
              onLabelCreated={handleLabelCreated}
              onFetch={handleFetch}
            />
          )}
          {selected === "Recent" && (
            <MemoFetchStories
              collapseAll={collapseAll}
              filterId={topicFilter}
              timestampRef={ts}
              recent={true}
              onLabelCreated={handleLabelCreated}
              onFetch={handleFetch}
            />
          )}
          {selected === "Events" && (
            <MemoFetchStories
              collapseAll={collapseAll}
              filterId={topicFilter}
              timestampRef={ts}
              events={true}
              onLabelCreated={handleLabelCreated}
              onFetch={handleFetch}
            />
          )}
          {selected === "Graph" && (
            <Box sx={{ height: "90vh", width: "100%" }}>
              <GraphView onStoryClicked={handleStoryClicked} />
            </Box>
          )}
          {selected === "Community" && (
            <Box sx={{ height: "90vh", width: "100%" }}>
              <UserGraph />
            </Box>
          )}
          {selected === "Vitals" && (
            <Box
              sx={{
                width: "100%",
                height: "90vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <InsightsIcon
                sx={{
                  fontSize: "64px",
                  color: "var(--text-secondary)",
                  opacity: 0.3,
                  mb: 1,
                }}
              />
              <Box
                sx={{
                  color: "var(--text-primary)",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                No vitals data available
              </Box>
              <Box
                sx={{
                  color: "var(--text-secondary)",
                  fontSize: "13px",
                  maxWidth: "400px",
                  textAlign: "center",
                }}
              >
                Team analytics, dynamics, and activity charts will appear here when available
              </Box>
            </Box>
          )}
          {selected === "Settings" && <Settings />}
          {selected === "People" && (
            <Box sx={{ maxWidth: 900 }}>
              <Box
                sx={{
                  mt: 1.5,
                  mb: 1.5,
                  ml: '40px',
                  px: 1,
                  py: 1,
                  borderRadius: '6px',
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(100, 150, 200, 0.08)'
                      : 'rgba(100, 150, 200, 0.06)',
                  border: (theme) =>
                    `1px solid ${theme.palette.mode === 'dark' ? 'rgba(100, 150, 200, 0.15)' : 'rgba(100, 150, 200, 0.12)'}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '13px',
                    lineHeight: 1.5,
                    color: 'var(--text-secondary)',
                  }}
                >
                  Your accounts across different platforms. Select multiple accounts to <strong>group</strong> them together as the same person,
                  or select grouped accounts to <strong>ungroup</strong> them. Click the arrow to view someone's stories.
                </Typography>
              </Box>
              <PeopleList />
            </Box>
          )}
        </Box>
        {selected === "Stories" && (
          <ComposeArea open={open} drawerWidth={drawerWidth} />
        )}
      </Main>
    </Box>
    </>
  );
}

function SideMenu({
  selected,
  setSelected,
}: {
  selected: string;
  setSelected: (category: MenuCategory) => void;
}) {
  const [todayEvents, setTodayEvents] = useState();
  const reloadData = useAtomValue(reloadState);
  const [httpPost, project] = useHttpPostAndProject();
  const storiesRef = useRef<HTMLDivElement>(null);

  const icons: any = {
    Stories: <AutoAwesomeMosaicIcon />,
    Artifacts: <MailIcon />,
    Deleted: <DeleteIcon />,
    Popular: <FavoriteIcon />,
    Recent: <TourIcon />,
    Events: (
      <Badge
        color="error"
        variant={selected === "Events" ? undefined : "dot"}
        invisible={!todayEvents}
        badgeContent={`${todayEvents}`}
      >
        <ScheduleIcon />
      </Badge>
    ),
    Graph: <ShareIcon />,
    Vitals: <InsightsIcon />,
    Settings: <SettingsIcon />,
    People: <PeopleAltIcon />,
    Community: <BubbleChartIcon />,
  };

  useEffect(() => {
    const request = async () => {
      try {
        const response = await httpPost("stories", {
          events_today: true,
          local_time: new Date().toString(),
        });
        setTodayEvents(response.data.events_today);
      } catch (err) {}
    };

    if (project) {
      request();
    }
  }, [project, reloadData]);

  useEffect(() => {
    if (selected === "Stories" && storiesRef.current) {
      storiesRef.current.focus();
    }
  }, [selected]);

  return (
    <List sx={{ px: 0.75, py: 0.5, pb: 1.5 }} dense>
      {menuCategoriesList.map((text, index) => {
        return (
          <ListItem key={index} disablePadding sx={{ mb: 0.125 }}>
            <ListItemButton
              sx={{
                borderRadius: '0 16px 16px 0',
                px: 1.5,
                py: 0.5,
                minHeight: '32px',
                width: '100%',
                transition: 'all 0.15s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'var(--surface-hover)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'var(--surface-selected)',
                  '&:hover': {
                    backgroundColor: 'var(--surface-selected)',
                    filter: 'brightness(0.95)',
                  },
                },
              }}
              selected={text === selected}
              onClick={() => setSelected(text)}
              ref={text === "Stories" ? storiesRef : undefined}
            >
              <ListItemIcon sx={{
                minWidth: '32px',
                color: text === selected ? 'var(--accent-primary)' : 'var(--text-secondary)',
                transition: 'color 0.15s ease-in-out',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.2rem',
                },
              }}>
                {icons[text]}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    fontWeight={text === selected ? 600 : 500}
                    fontSize={13}
                    sx={{
                      color: text === selected ? 'var(--text-primary)' : 'var(--text-secondary)',
                      transition: 'color 0.15s ease-in-out',
                      lineHeight: 1.4,
                    }}
                  >
                    {text}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}

function RawMessages({ filterId }: { filterId?: string }) {
  const [data, setData] = useState<MessageItem[]>();
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timeFilter = useAtomValue(timeFilterState);
  const reloadData = useAtomValue(reloadState);
  const [httpPost, project] = useHttpPostAndProject();

  const getData = async () => {
    try {
      setLoading(true);
      const response = await httpPost("stories", {
        showlog: true,
        period: timeFilter,
      });

      const decompress = async (log: any[]): Promise<MessageItem[]> => {
        const out: MessageItem[] = [];
        for (const msg of log) {
          if (msg.gzip) {
            const obj = await u.decompressObject(msg.gzip);
            delete msg.gzip;
            out.push({ ...msg, ...obj });
          } else {
            out.push(msg);
          }
        }
        return out;
      };

      if (!data || data.length !== response.data.showlog.length) {
        if (!filterId) {
          setData(await decompress(response.data.showlog));
        } else {
          const data = response.data.showlog.filter(
            (item: MessageItem) => item.SK === filterId
          );
          setData(await decompress(data));
        }
        setError(null);
      }
    } catch (err: any) {
      log.debug(err);
      if (!data) {
        setError(err.message);
        setData(undefined);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (project) {
      getData();
    }
  }, [reloadData, timeFilter, project]);

  return (
    <Box>
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography sx={{ color: 'text.secondary' }}>Loading data...</Typography>
        </Box>
      )}
      {error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography sx={{ color: 'text.secondary' }}>Failed to fetch data</Typography>
        </Box>
      )}

      {data && (
        <Thread
          messages={data}
          allowReplay={true}
          expand={filterId ? true : false}
        />
      )}
    </Box>
  );
}

const MemoFetchStories = memo(FetchStories);

function AuthStage({ stage }: { stage: string }) {
  switchStage(stage);

  const components = {
    Header() {
      return (
        <Box className="login-logo-container">
          <Logo height={36} />
        </Box>
      );
    },
    Footer() {
      return (
        <Box className="login-footer-container">
          <Typography className="login-copyright">
            © 2026 topstorie.com. All rights reserved.
          </Typography>
        </Box>
      );
    },
  };

  return (
    <Authenticator components={components} hideSignUp={true}>
      {({ signOut, user }) => {
        return <Home signOut={signOut} />;
      }}
    </Authenticator>
  );
}

function AppRoutes() {
  const stage = useStage();

  return (
    <Routes>
      <Route path="/" element={<AuthStage stage={stage} />} />
      <Route
        path=":type/:proj/:id?/:ts?"
        element={<AuthStage stage={stage} />}
      />
      <Route path=":proj" element={<AuthStage stage={stage} />} />
    </Routes>
  );
}

function App() {
  const [wasmLoaded, setWasmLoaded] = useState(false);
  const themeMode = useAtomValue(themeModeState);
  const effectiveTheme = getEffectiveTheme(themeMode);
  const theme = effectiveTheme === 'dark' ? darkTheme : lightTheme;

  useEffect(() => {
    init().then((e) => {
      setWasmLoaded(true);
    });
  });

  // Listen for system theme changes when in system mode
  useEffect(() => {
    if (themeMode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      // Force re-render by updating a dummy state
      setWasmLoaded((prev) => prev);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  // Apply data-theme attribute for CSS variables
  useEffect(() => {
    const root = document.documentElement;
    const isDark = effectiveTheme === 'dark';

    if (isDark) {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }

    // Set Markdown-specific CSS variables
    root.style.setProperty('--md-code-bg', isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)');
    root.style.setProperty('--md-code-text', isDark ? '#d1d5db' : '#4b5563');
    root.style.setProperty('--md-username-bg', isDark ? 'rgba(255, 255, 255, 0.15)' : '#e0f0f9ff');
    root.style.setProperty('--md-highlight-bg', isDark ? '#d97706' : '#ffeb3b');
    root.style.setProperty('--md-highlight-text', isDark ? '#1f2937' : 'inherit');
  }, [effectiveTheme]);

  return (
    <ThemeProvider theme={theme}>
      {wasmLoaded && (
        <Router>
          <AppRoutes />
        </Router>
      )}
    </ThemeProvider>
  );
}

function TimeFilters() {
  const alltime = String.fromCodePoint(0x221e);
  const filters = ["1d", "2d", "1w", "1m", alltime];
  const [selected, setSelected] = useAtom(timeFilterState);

  const handleClick = (e: React.MouseEvent<HTMLElement>, item: string) => {
    if (item !== selected) {
      setSelected(item);
    }
  };

  const getChipStyle = (item: string, theme: any) => {
    const isSelected = item === selected;
    const isAllTime = item === alltime;

    if (isSelected) {
      if (isAllTime) {
        // All time - Green
        return {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(129, 201, 149, 0.24)' : '#e6f4ea',
          color: theme.palette.mode === 'dark' ? '#81c995' : '#137333',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(129, 201, 149, 0.4)' : '#81c995',
        };
      } else {
        // Other selected - Blue
        return {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(138, 180, 248, 0.24)' : '#d3e3fd',
          color: theme.palette.mode === 'dark' ? '#8ab4f8' : '#1967d2',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(138, 180, 248, 0.4)' : '#8ab4f8',
        };
      }
    } else {
      // Not selected
      return {
        backgroundColor: 'transparent',
        color: theme.palette.mode === 'dark' ? '#9aa0a6' : '#5f6368',
        borderColor: theme.palette.mode === 'dark' ? 'rgba(232, 234, 237, 0.2)' : '#dadce0',
      };
    }
  };

  return (
    <Box sx={{ px: 1.25, py: 0.5 }}>
      <Typography
        fontSize={11}
        fontWeight={600}
        sx={(theme) => ({
          color: theme.palette.mode === 'dark' ? '#9aa0a6' : '#5f6368',
          mb: 0.75,
          letterSpacing: '0.3px',
          textTransform: 'uppercase',
        })}
      >
        Time Range
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {filters.map((item) => (
          <Chip
            key={item}
            size="small"
            label={item}
            variant="outlined"
            onClick={(e) => {
              handleClick(e, item);
            }}
            sx={(theme) => ({
              fontWeight: item === selected ? 500 : 400,
              fontSize: '0.7rem',
              height: '22px',
              borderRadius: '11px',
              transition: 'all 0.15s cubic-bezier(0.4, 0.0, 0.2, 1)',
              ...getChipStyle(item, theme),
              '&:hover': {
                backgroundColor: item !== selected
                  ? (theme.palette.mode === 'dark' ? 'rgba(232, 234, 237, 0.08)' : '#f1f3f4')
                  : undefined,
              },
              '& .MuiChip-label': {
                px: 1.25,
              },
            })}
          />
        ))}
      </Stack>
    </Box>
  );
}

export default App;
